import React from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { withStyles, lighten } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import Box from '@material-ui/core/Box';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Fab from '@material-ui/core/Fab';
import GetAppIcon from '@material-ui/icons/GetApp';
import PublicIcon from '@material-ui/icons/Public';
import Embed from './Embed';
import File from './File';
import withStores from '../../stores/withStores';
import useI18n from '../../i18n/useI18n';

const styles = theme => ({
  root: {
    position: 'relative',
    height: '100%',
    minHeight: 150,
    // background: theme.palette.grey['100'],
    zIndex: 0,
    // '&:hover': {
    //   '&:before': {
    //     background: 'rgba(0,0,0,0.3)',
    //   },
    // },
    '&:before': {
      pointerEvents: 'none',
      position: 'absolute',
      content: '""',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      zIndex: 1,
      background: 'rgba(0,0,0,0)',
    },
    '&:hover $downloadButton': {
      visibility: 'visible',
    },
  },
  downloadButton: {
    zIndex: 1,
    visibility: 'hidden',
    backgroundColor: theme.editorPalette.primary.main,
    color: theme.editorPalette.common.white,
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    '&:hover': {
      backgroundColor: theme.editorPalette.primary.dark,
    },
  },
  copyright: {
    position: 'absolute',
    bottom: 4,
    left: 0,
    width: '100%',
    paddingRight: theme.spacing(2),
    background: `linear-gradient(to left, rgba(255,255,255,1) 0%, rgba(255,255,255,0.76) 47%, rgba(255,255,255,0.20) 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    '& > span': {
      marginBottom: 0,
    },
  },
  urlDisplay: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    // outline: `2px solid ${theme.editorPalette.primary.light}`,
    backgroundColor: lighten(theme.editorPalette.primary.light, 0.5),
    width: '100%',
    height: '100%',
    '& svg': {
      fill: theme.editorPalette.primary.dark,
    },
    '& p': {
      marginBottom: 0,
    },
  },
});

const DownloadButton = props => {
  const { item, className } = props;
  const i18n = useI18n();
  return (
    <Tooltip
      title={i18n.t('Media.Preview.DownloadButton.Tooltip.title')}
      placement="top"
    >
      <Fab
        href={`/ada-assets/${item.uuid}`}
        download={item.titel || item.originalFilename}
        color="inherit"
        className={className}
      >
        <GetAppIcon />
      </Fab>
    </Tooltip>
  );
};

const Copyright = props => {
  const { item, className } = props;
  const { author, copyright } = item;
  const hasCopyright = author || copyright;
  if (!hasCopyright) return null;
  return (
    <Box className={className}>
      <Typography variant="caption">
        {copyright} {copyright && author && `|`} {author}
      </Typography>
    </Box>
  );
};

const URLDisplay = props => {
  const { item, className } = props;
  return (
    <Box
      component="a"
      className={className}
      href={item.source}
      target="_blank"
      rel="noopener noreferrer"
    >
      <Box mb={1}>
        <PublicIcon color="currentColor" fontSize="large" />
      </Box>
      <Typography color="inherit" variant="body1">
        {item.title || item.source}
      </Typography>
    </Box>
  );
};

@withStores('mediaStore')
@observer
class Preview extends React.Component {
  static propTypes = {
    item: PropTypes.object.isRequired,
    classes: PropTypes.object.isRequired,
    noMinifcation: PropTypes.bool,
    noInteraction: PropTypes.bool,
    refreshId: PropTypes.string,
  };
  static defaultProps = {
    noMinifcation: false,
    noInteraction: false,
    refreshId: null,
  };

  get preview() {
    const {
      item,
      classes,
      noMinifcation,
      noInteraction,
      refreshId,
    } = this.props;
    switch (item.class) {
      case 'file':
        return (
          <React.Fragment>
            <DownloadButton item={item} className={classes.downloadButton} />
            <File
              type={item.sourceType}
              item={item}
              noMinifcation={noMinifcation}
              noInteraction={noInteraction}
              refreshId={refreshId}
            />
          </React.Fragment>
        );
      case 'embed':
        return (
          <Embed
            type={item.class}
            src={item.source}
            noInteraction={noInteraction}
          />
        );
      case 'URL':
        return <URLDisplay className={classes.urlDisplay} item={item} />;
      default:
        return <Typography variant="body2">{item.type}</Typography>;
    }
  }

  render() {
    const { classes, className, item } = this.props;
    return (
      <div className={cx(classes.root, className)}>
        {this.preview}
        <Copyright item={item} className={classes.copyright} />
      </div>
    );
  }
}

export default withStyles(styles)(Preview);
